// XFDF: the interchange format for "here is what was written on this
// document, separately from the document". Two independent halves, both
// handled here:
//
//   <fields> — form answers. Acrobat, Foxit and most server-side
//     tooling read and write it, so this is what lets a form filled
//     here be handed to somebody else's workflow.
//   <annots> — comments: highlights, notes, drawings, shapes. Same
//     deal for review round-trips (see xfdf-annots.ts, which owns the
//     annotation mapping and builds on the parser below).
//
// The remaining parts of the spec (page-level data, embedded files) are
// not handled; pretending to support them here would round-trip them
// into oblivion.
//
// The parser is hand-rolled rather than DOMParser-based so it runs and
// is testable outside a browser. XFDF is machine-generated and
// structurally simple, which makes that trade sound — but it is a
// subset parser, not a conformant XML one: it understands elements,
// attributes, text, CDATA and the standard entities, and nothing more
// exotic.

const XML_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
};

export function decodeXmlText(raw: string): string {
  return raw.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (whole, body: string) => {
    if (body.startsWith("#x") || body.startsWith("#X")) {
      const code = Number.parseInt(body.slice(2), 16);
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole;
    }
    if (body.startsWith("#")) {
      const code = Number.parseInt(body.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole;
    }
    return XML_ENTITIES[body] ?? whole;
  });
}

export function encodeXmlText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ─────────────────────────── generic XML ───────────────────────────

/** One element. `name` has any namespace prefix stripped — writers
 *  disagree about whether to use one, and nothing here depends on which
 *  namespace an element came from. `text` is this element's own text
 *  (direct children's text is not folded in; use `allText` for that). */
export interface XmlNode {
  name: string;
  attrs: Record<string, string>;
  children: XmlNode[];
  text: string;
}

/** Strip the namespace prefix — writers differ on whether they use one. */
function localName(name: string): string {
  const i = name.indexOf(":");
  return i < 0 ? name : name.slice(i + 1);
}

interface Tag {
  name: string;
  attrs: Record<string, string>;
  selfClosing: boolean;
  closing: boolean;
}

function parseTag(source: string): Tag | null {
  const m = /^<\s*(\/?)\s*([\w:.-]+)([\s\S]*?)(\/?)\s*>$/.exec(source);
  if (!m) return null;
  const attrs: Record<string, string> = {};
  for (const a of m[3]!.matchAll(/([\w:.-]+)\s*=\s*"([^"]*)"|([\w:.-]+)\s*=\s*'([^']*)'/g)) {
    const key = a[1] ?? a[3];
    const val = a[2] ?? a[4] ?? "";
    if (key) attrs[localName(key)] = decodeXmlText(val);
  }
  return {
    name: localName(m[2]!),
    attrs,
    selfClosing: m[4] === "/",
    closing: m[1] === "/",
  };
}

/** Parse a document into a tree, returning the root element (null if
 *  there isn't one). Malformed input yields whatever structure could be
 *  recovered rather than an exception: an XFDF file that a reader can't
 *  make sense of should import as "nothing found", not as a crash. */
export function parseXml(xml: string): XmlNode | null {
  let root: XmlNode | null = null;
  const stack: XmlNode[] = [];
  const top = (): XmlNode | undefined => stack[stack.length - 1];
  let i = 0;

  const addText = (s: string) => {
    const node = top();
    if (node && s) node.text += s;
  };

  while (i < xml.length) {
    const lt = xml.indexOf("<", i);
    if (lt < 0) {
      addText(decodeXmlText(xml.slice(i)));
      break;
    }
    addText(decodeXmlText(xml.slice(i, lt)));

    // CDATA is text, whatever it contains.
    if (xml.startsWith("<![CDATA[", lt)) {
      const end = xml.indexOf("]]>", lt);
      addText(xml.slice(lt + 9, end < 0 ? xml.length : end));
      i = end < 0 ? xml.length : end + 3;
      continue;
    }
    // Comments, processing instructions and doctypes never carry data.
    if (xml.startsWith("<!--", lt)) {
      const end = xml.indexOf("-->", lt);
      i = end < 0 ? xml.length : end + 3;
      continue;
    }
    if (xml.startsWith("<?", lt)) {
      const end = xml.indexOf("?>", lt);
      i = end < 0 ? xml.length : end + 2;
      continue;
    }
    if (xml.startsWith("<!", lt)) {
      const end = xml.indexOf(">", lt);
      i = end < 0 ? xml.length : end + 1;
      continue;
    }

    const gt = xml.indexOf(">", lt);
    if (gt < 0) break;
    const tag = parseTag(xml.slice(lt, gt + 1));
    i = gt + 1;
    if (!tag) continue;

    if (tag.closing) {
      // Close the nearest matching ancestor. A stray close tag for
      // something that was never opened is ignored, not fatal.
      const at = stack.map((n) => n.name).lastIndexOf(tag.name);
      if (at >= 0) stack.length = at;
      continue;
    }

    const node: XmlNode = { name: tag.name, attrs: tag.attrs, children: [], text: "" };
    const parent = top();
    if (parent) parent.children.push(node);
    else if (!root) root = node;
    else continue; // a second root element — ignore the extra tree
    if (!tag.selfClosing) stack.push(node);
  }
  return root;
}

/** This element's text plus all of its descendants', in document order. */
export function allText(node: XmlNode): string {
  return node.children.reduce((acc, c) => acc + allText(c), node.text);
}

/** Direct children with the given local name. */
export function childrenNamed(node: XmlNode, name: string): XmlNode[] {
  return node.children.filter((c) => c.name === name);
}

/** First descendant (self included) with the given local name. */
export function findNode(node: XmlNode, name: string): XmlNode | null {
  if (node.name === name) return node;
  for (const c of node.children) {
    const hit = findNode(c, name);
    if (hit) return hit;
  }
  return null;
}

// ─────────────────────────── form fields ───────────────────────────

/** Build an XFDF document from a name → value map.
 *
 *  Names are written FLAT and fully qualified ("address.city"), not
 *  nested. Both forms are legal and every reader accepts the flat one,
 *  while nesting invites disagreement about where a literal dot in a
 *  field name ends and a hierarchy separator begins. */
export function buildXfdf(values: Record<string, string>, href?: string): string {
  const fields = Object.keys(values)
    .sort()
    .map((name) => {
      const value = values[name] ?? "";
      return (
        `    <field name="${encodeXmlText(name)}">\n` +
        `      <value>${encodeXmlText(value)}</value>\n` +
        `    </field>`
      );
    })
    .join("\n");
  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">\n` +
    (href ? `  <f href="${encodeXmlText(href)}"/>\n` : "") +
    `  <fields>\n${fields}${fields ? "\n" : ""}  </fields>\n` +
    `</xfdf>\n`
  );
}

/** Read the field values out of an XFDF document.
 *
 *  Handles both the flat form this module writes and the nested form
 *  Acrobat writes for hierarchical names, joining nested names with "."
 *  so either input produces the same fully-qualified keys the editor's
 *  form state uses. A field with several `<value>` elements (a list box
 *  with more than one selection) keeps the first — the editor's form
 *  model holds one string per field. */
export function parseXfdf(xml: string): Record<string, string> {
  const out: Record<string, string> = {};
  const root = parseXml(xml);
  const fields = root && findNode(root, "fields");
  if (!fields) return out;

  const walk = (node: XmlNode, prefix: readonly string[]): void => {
    for (const f of childrenNamed(node, "field")) {
      const path = f.attrs.name ? [...prefix, f.attrs.name] : [...prefix];
      const key = path.join(".");
      const value = childrenNamed(f, "value")[0];
      // A field with no <value> says nothing about it, which is not the
      // same as an empty answer. First value wins: the editor stores one
      // string per field.
      if (value && key && !(key in out)) out[key] = allText(value);
      walk(f, path);
    }
  };
  walk(fields, []);
  return out;
}

export interface XfdfApplyResult {
  /** Values for fields this document actually has. */
  applied: Record<string, string>;
  /** Names in the file with no matching field here. */
  unmatched: string[];
  /** Fields in the document the file said nothing about. */
  untouched: string[];
}

/** Reconcile imported data against the form that's actually open. Names
 *  that don't exist here are reported rather than silently dropped —
 *  importing the wrong file should say so, not appear to work. */
export function applyXfdfValues(
  imported: Record<string, string>,
  fieldNames: readonly string[]
): XfdfApplyResult {
  const known = new Set(fieldNames);
  const applied: Record<string, string> = {};
  const unmatched: string[] = [];
  for (const [name, value] of Object.entries(imported)) {
    if (known.has(name)) applied[name] = value;
    else unmatched.push(name);
  }
  const untouched = fieldNames.filter((n) => !(n in imported));
  return { applied, unmatched, untouched };
}
